import { consola } from 'consola'
import { clearPushNotificationSettingsStorage } from '@shared/lib'
/**
 * Composable для работы с настройками push уведомлений
 * Использует Capacitor SecureStorage для нативных приложений
 * Fallback на localStorage для веб-версии
 */
export function usePushNotificationStorage() {
  
  /**
   * Сохраняет значение в storage
   */
  const setItem = async (key: string, value: string): Promise<void> => {
    try {
      if (process.client) {
        const { Capacitor } = await import('@capacitor/core')
        
        if (Capacitor.isNativePlatform()) {
          // Используем SecureStorage для нативных приложений
          const { SecureStoragePlugin } = await import('capacitor-secure-storage-plugin')
          await SecureStoragePlugin.set({ key, value })
        } else {
          // Fallback на localStorage для веб
          localStorage.setItem(key, value)
        }
      }
    } catch (error) {
      consola.warn(`[PushStorage] Failed to save ${key}:`, error)
      // Fallback на localStorage в случае ошибки
      if (process.client) {
        localStorage.setItem(key, value)
      }
    }
  }

  /**
   * Получает значение из storage
   */
  const getItem = async (key: string): Promise<string | null> => {
    try {
      if (process.client) {
        const { Capacitor } = await import('@capacitor/core')
        
        if (Capacitor.isNativePlatform()) {
          // Используем SecureStorage для нативных приложений
          const { SecureStoragePlugin } = await import('capacitor-secure-storage-plugin')
          const result = await SecureStoragePlugin.get({ key })
          return result?.value || null
        } else {
          // Fallback на localStorage для веб
          return localStorage.getItem(key)
        }
      }
      return null
    } catch (error) {
      consola.warn(`[PushStorage] Failed to get ${key}:`, error)
      // Fallback на localStorage в случае ошибки
      if (process.client) {
        return localStorage.getItem(key)
      }
      return null
    }
  }

  /**
   * Удаляет значение из storage
   */
  const removeItem = async (key: string): Promise<void> => {
    try {
      if (process.client) {
        const { Capacitor } = await import('@capacitor/core')
        
        if (Capacitor.isNativePlatform()) {
          // Используем SecureStorage для нативных приложений
          const { SecureStoragePlugin } = await import('capacitor-secure-storage-plugin')
          await SecureStoragePlugin.remove({ key })
        } else {
          // Fallback на localStorage для веб
          localStorage.removeItem(key)
        }
      }
    } catch (error) {
      consola.warn(`[PushStorage] Failed to remove ${key}:`, error)
      // Fallback на localStorage в случае ошибки
      if (process.client) {
        localStorage.removeItem(key)
      }
    }
  }

  // Константы для ключей
  const KEYS = {
    PUSH_ENABLED: 'pushNotificationsEnabled',
    PUSH_ASKED: 'pushNotificationsAsked', 
    PUSH_TOKEN: 'pushNotificationToken',
  } as const

  /**
   * Специфичные методы для push уведомлений
   */
  const setPushEnabled = async (enabled: boolean): Promise<void> => {
    await setItem(KEYS.PUSH_ENABLED, enabled.toString())
  }

  const isPushEnabled = async (): Promise<boolean> => {
    const value = await getItem(KEYS.PUSH_ENABLED)
    const result = value === 'true'
    consola.debug('🔔 [STORAGE DEBUG] isPushEnabled - value:', value, 'result:', result)
    return result
  }

  const setPushAsked = async (asked: boolean): Promise<void> => {
    await setItem(KEYS.PUSH_ASKED, asked.toString())
  }

  const wasPushAsked = async (): Promise<boolean> => {
    const value = await getItem(KEYS.PUSH_ASKED)
    const result = value === 'true'
    consola.debug('🔔 [STORAGE DEBUG] wasPushAsked - value:', value, 'result:', result)
    return result
  }

  const setPushToken = async (token: string): Promise<void> => {
    await setItem(KEYS.PUSH_TOKEN, token)
  }

  const getPushToken = async (): Promise<string | null> => {
    return await getItem(KEYS.PUSH_TOKEN)
  }

  const removePushToken = async (): Promise<void> => {
    await removeItem(KEYS.PUSH_TOKEN)
  }

  /**
   * Очищает все настройки push уведомлений
   */
  const clearPushSettings = async (): Promise<void> => {
    await clearPushNotificationSettingsStorage()
  }

  return {
    // Общие методы
    setItem,
    getItem,
    removeItem,
    
    // Специфичные для push уведомлений
    setPushEnabled,
    isPushEnabled,
    setPushAsked,
    wasPushAsked,
    setPushToken,
    getPushToken,
    removePushToken,
    clearPushSettings,
    
    // Константы
    KEYS,
  }
}
